/* eslint-disable no-console */
/**
 * Browser Clipboard API helpers. Failures are returned (no throws) and logged for debugging.
 *
 * Sync checks use only API *presence* (e.g. `typeof navigator.clipboard?.writeText === 'function'`).
 * That does not invoke `writeText`, so nothing is written to the clipboard during the check.
 *
 * When the async Clipboard API is unavailable (e.g. plain HTTP), text copy falls back to a hidden
 * textarea + `document.execCommand('copy')` (deprecated but still works in many browsers when
 * triggered from a user gesture). Image copy has no such fallback — use the Clipboard API or a
 * separate download/export control.
 */

const LOG_PREFIX = '[@nova/utils clipboard]';

const CLIPBOARD_PNG_MIME = 'image/png';

export type ClipboardCopyResult = { ok: true } | { ok: false; toastMessage: string };

/** Short toast when the Clipboard API is missing or the page is not a secure context. */
export const CLIPBOARD_TOAST_UNAVAILABLE = 'Clipboard unavailable';

/** Short toast when write fails (permissions, transient errors, etc.). */
export const CLIPBOARD_TOAST_TEXT_FAILED = 'Could not copy';

/** Short toast when PNG blob cannot be built from the canvas. */
export const CLIPBOARD_TOAST_IMAGE_PREP_FAILED = 'Could not prepare image';

/** Short toast when image clipboard is not supported. */
export const CLIPBOARD_TOAST_IMAGE_UNSUPPORTED = 'Image copy not supported here';

/** Short toast when image write fails. */
export const CLIPBOARD_TOAST_IMAGE_FAILED = 'Could not copy image';

/** Use as `title` on disabled “copy text” controls. */
export const CLIPBOARD_TOOLTIP_TEXT_UNAVAILABLE = 'Clipboard is not available. Use HTTPS or localhost, or try another browser.';

/** Use as `title` on disabled “copy image” controls. */
export const CLIPBOARD_TOOLTIP_IMAGE_UNAVAILABLE = 'Copying images to the clipboard is not supported in this browser.';

function logUnavailableTextReason(): void {
  console.warn(`${LOG_PREFIX} Text copy unavailable.`, {
    isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
    hasNavigatorClipboard: typeof navigator !== 'undefined' && !!navigator.clipboard,
    hasWriteText:
      typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function',
    detail: 'Requires a secure context (HTTPS or localhost) and navigator.clipboard.writeText.'
  });
}

function logUnavailableImageReason(): void {
  console.warn(`${LOG_PREFIX} Image copy unavailable.`, {
    textOk: isClipboardTextWriteAvailable(),
    hasClipboardWrite:
      typeof navigator !== 'undefined' && typeof navigator.clipboard?.write === 'function',
    hasClipboardItem: typeof ClipboardItem !== 'undefined',
    detail: 'Requires ClipboardItem, navigator.clipboard.write, and the same secure context as text.'
  });
}

export function isClipboardTextWriteAvailable(): boolean {
  if (typeof window === 'undefined') { return false; }
  return window.isSecureContext && typeof navigator.clipboard?.writeText === 'function';
}

export function isExecCommandCopyAvailable(): boolean {
  if (typeof document === 'undefined') { return false; }
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- execCommand is deprecated; used as fallback for text copy when Clipboard API is unavailable
  return typeof document.execCommand === 'function';
}

/**
 * True if text copy is likely to work: modern Clipboard API or legacy execCommand path.
 * The legacy path can still fail at runtime (e.g. user gesture required); there is no perfect sync check.
 */
export function isTextCopyToClipboardAvailable(): boolean {
  if (typeof window === 'undefined') { return false; }
  return isClipboardTextWriteAvailable() || isExecCommandCopyAvailable();
}

/** True if image clipboard write appears supported (sync; does not write). */
export function isClipboardImageWriteAvailable(): boolean {
  return (
    isClipboardTextWriteAvailable()
    && typeof navigator.clipboard?.write === 'function'
    && typeof ClipboardItem === 'function'
  );
}

function copyTextViaExecCommand(text: string): boolean {
  if (!isExecCommandCopyAvailable()) { return false; }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  ta.style.top = '0';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  ta.setSelectionRange(0, text.length);
  let ok;
  try {
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- legacy text copy when navigator.clipboard.writeText is unavailable (e.g. HTTP)
    ok = document.execCommand('copy');
  }
  catch {
    ok = false;
  }
  finally {
    document.body.removeChild(ta);
  }
  return ok;
}

export async function copyTextToClipboard(text: string): Promise<ClipboardCopyResult> {
  if (isClipboardTextWriteAvailable()) {
    try {
      await navigator.clipboard.writeText(text);
      return { ok: true };
    }
    catch (e) {
      console.error(`${LOG_PREFIX} writeText failed:`, e);
      if (copyTextViaExecCommand(text)) {
        return { ok: true };
      }
      return { ok: false, toastMessage: CLIPBOARD_TOAST_TEXT_FAILED };
    }
  }

  if (copyTextViaExecCommand(text)) {
    return { ok: true };
  }
  logUnavailableTextReason();
  return { ok: false, toastMessage: CLIPBOARD_TOAST_UNAVAILABLE };
}

export async function copyPngFromCanvas(canvas: HTMLCanvasElement): Promise<ClipboardCopyResult> {
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), CLIPBOARD_PNG_MIME);
  });
  if (!blob) {
    console.error(`${LOG_PREFIX} canvas.toBlob returned null`, {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });
    return { ok: false, toastMessage: CLIPBOARD_TOAST_IMAGE_PREP_FAILED };
  }

  if (!isClipboardImageWriteAvailable()) {
    logUnavailableImageReason();
    return { ok: false, toastMessage: CLIPBOARD_TOAST_IMAGE_UNSUPPORTED };
  }

  try {
    await navigator.clipboard.write([new ClipboardItem({ [CLIPBOARD_PNG_MIME]: blob })]);
    return { ok: true };
  }
  catch (e) {
    console.error(`${LOG_PREFIX} write(image/png) failed:`, e);
    return { ok: false, toastMessage: CLIPBOARD_TOAST_IMAGE_FAILED };
  }
}
